import PageHero from "@/components/PageHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import TranslatedProse from "@/components/TranslatedProse";
import { motion } from "framer-motion";

const HERO_ART = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/oj2lps48_naturepulse-illustration.jpg";

const IMG = {
  forest: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1600&auto=format&fit=crop",
  storm: "https://images.pexels.com/photos/4028958/pexels-photo-4028958.jpeg?auto=compress&cs=tinysrgb&w=1600",
  wildlife: "https://images.pexels.com/photos/29069019/pexels-photo-29069019.jpeg?auto=compress&cs=tinysrgb&w=1600",
  sunset: "https://images.unsplash.com/photo-1519861531473-9200262188bf?w=1600&auto=format&fit=crop",
  bird: "https://images.unsplash.com/photo-1591824438708-ce405f36ba3d?w=1600&auto=format&fit=crop",
  mountain: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1600&auto=format&fit=crop",
  aurora: "https://images.pexels.com/photos/31585091/pexels-photo-31585091.jpeg?auto=compress&cs=tinysrgb&w=1600",
  turbines: "https://images.pexels.com/photos/30037318/pexels-photo-30037318.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

function Chapter({ eyebrow, title, children, id }) {
  return (
    <section id={id} className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-15%" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">{eyebrow}</div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter mb-8">{title}</h2>
        <div className="space-y-6 text-lg leading-relaxed text-[color:var(--np-text-2)]">
          {children}
        </div>
      </motion.div>
    </section>
  );
}

function ImagePlate({ src, alt, caption, aspect = "aspect-[16/9]" }) {
  return (
    <motion.figure
      initial={{ opacity: 0, y: 30, scale: 0.98 }}
      whileInView={{ opacity: 1, y: 0, scale: 1 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-6xl mx-auto px-6 md:px-10 my-16 md:my-24"
      data-testid={`about-image-${alt.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`relative w-full ${aspect} overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_40px_100px_-40px_rgba(228,87,46,0.35)]`}>
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      </div>
      {caption && (
        <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          {caption}
        </figcaption>
      )}
    </motion.figure>
  );
}

export default function About() {
  return (
    <TranslatedProse cacheKey="about">
      <PageHero
        eyebrow="/ About · Welcome"
        title="Welcome to NaturePulse."
        subtitle="Discover Nature. Understand Weather. Protect Our Planet."
        image={IMG.sunset}
      />

      <EditorialMarquee terms={["EST. 2025", "A SOCIAL PULSE ENDEAVOR", "OPEN DATA", "COMMUNITY OBSERVATION", "EVERY DAY · EVERY PLACE · EVERY SPECIES"]} />

      {/* Epigraph */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <motion.blockquote
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-serif-display text-3xl md:text-5xl leading-[1.15] tracking-tight text-[color:var(--np-text)]"
        >
          <span className="text-[color:var(--np-brand)]">“</span>
          Every sunrise tells a story. Every forest holds a history. Every storm leaves a lesson. Every observation helps us understand our changing world.
          <span className="text-[color:var(--np-brand)]">”</span>
        </motion.blockquote>
      </section>

      <Chapter eyebrow="/01 · The foundation" title="Nature is the foundation of all life.">
        <p>
          From the tallest mountains to the deepest oceans, from the smallest insects to the largest whales, from ancient forests to modern cities, our planet is a living, interconnected system that changes every day.
        </p>
        <p>
          NaturePulse, part of the SocialPulse ecosystem, is a global platform dedicated to documenting, understanding, and celebrating the natural world. It brings together nature, wildlife, weather, climate, landscapes, and environmental history into one living archive that anyone can explore and contribute to.
        </p>
        <p>
          Whether you're tracking a rare bird, photographing a wildflower, documenting a powerful storm, or simply admiring a beautiful sunset, every observation helps tell the story of our planet.
        </p>
      </Chapter>

      {/* Featured illustration */}
      <motion.figure
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10%" }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="max-w-6xl mx-auto px-6 md:px-10 my-8 md:my-16"
        data-testid="about-hero-illustration"
      >
        <div className="relative w-full overflow-hidden bg-[color:var(--np-bg-2)] shadow-[0_50px_120px_-40px_rgba(228,87,46,0.45)]">
          <img
            src={HERO_ART}
            alt="NaturePulse — Discover Nature. Follow the Weather. Understand Climate. Protect Our Future."
            className="w-full h-auto"
            loading="lazy"
          />
        </div>
        <figcaption className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted)]">
          Discover Nature · Follow the Weather · Understand Climate · Protect Our Future
        </figcaption>
      </motion.figure>

      <Chapter eyebrow="/02 · A living record of Earth" title="More than a website. A memory.">
        <p>
          NaturePulse is more than a nature website or a weather service. It is a living environmental archive that captures the changing face of Earth, one day at a time.
        </p>
        <div className="mt-4">
          <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-4">Explore</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-[color:var(--np-text)] font-serif-display text-xl leading-snug">
            <li>🌍 Countries, regions, cities & landscapes</li>
            <li>🌿 Plants, forests, flowers & ecosystems</li>
            <li>🐘 Wildlife, birds, insects, fish & marine life</li>
            <li>🏔 Mountains, rivers, lakes, beaches & deserts</li>
            <li>🌅 Sunrises, sunsets, clouds & night skies</li>
            <li>🌦 Daily weather conditions and forecasts</li>
            <li>🌪 Historic storms and the Weather Almanac</li>
            <li>🌡 Climate records and environmental trends</li>
            <li>📸 Community photos, videos & observations</li>
          </ul>
        </div>
        <p className="mt-4">
          Every location becomes a living story that grows richer with each season and every contribution from the community.
        </p>
      </Chapter>

      <ImagePlate src={IMG.forest} alt="Ancient forest at dawn" caption="Ancient forest · living archive · every canopy is a chronicle" />

      <Chapter eyebrow="/03 · Understanding global warming" title="Climate change is the defining challenge of our time.">
        <p>
          Around the world we are witnessing changing weather patterns, rising temperatures, more frequent heatwaves, intense rainfall, prolonged droughts, stronger storms, melting glaciers, shifting ecosystems, and changing migration patterns for wildlife. Understanding these changes requires accurate, long-term observations — not just isolated events.
        </p>
        <p>
          NaturePulse is committed to building one of the world's most comprehensive environmental records by documenting nature and weather every day. Through historical records, scientific data, satellite imagery, community observations, and local knowledge, we can better understand how our planet is changing over time.
        </p>
        <p>
          The goal is not only to preserve history but to help individuals, communities, researchers, educators, and future generations learn from it.
        </p>
      </Chapter>

      <ImagePlate src={IMG.storm} alt="Storm system building over the ocean" caption="Storm systems · frequency and intensity increasing" />

      <Chapter eyebrow="/04 · The Weather Almanac" title="A record of every storm, every drought, every record broken.">
        <p>
          NaturePulse includes a global <span className="text-[color:var(--np-brand)] font-medium">Weather Almanac</span>, documenting significant weather events from every corner of the world.
        </p>
        <p>
          From hurricanes and cyclones to blizzards, floods, droughts, wildfires, and record-breaking temperatures — every event becomes part of a searchable historical archive complete with maps, timelines, photographs, videos, scientific information, and community experiences.
        </p>
        <p>
          By preserving the past, we improve our understanding of the future and strengthen our ability to prepare for changing weather conditions.
        </p>
      </Chapter>

      <ImagePlate src={IMG.bird} alt="Bird in flight" caption="Migration is memory · every wing carries the record" />

      <Chapter eyebrow="/05 · A community for nature lovers" title="Nature belongs to everyone.">
        <p>NaturePulse invites people from around the world to share:</p>
        <ul className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-[color:var(--np-text)] font-serif-display text-lg">
          {["Wildlife sightings","Nature photography","Weather observations","Seasonal changes","Flower blooms","Bird migrations","Scenic landscapes","Local environmental stories","Historic weather memories","Conservation initiatives"].map(x => (
            <li key={x} className="flex items-baseline gap-3">
              <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--np-brand)] inline-block" />
              {x}
            </li>
          ))}
        </ul>
        <p>Every photograph, every observation, and every story contributes to a richer understanding of our planet.</p>
      </Chapter>

      <ImagePlate src={IMG.aurora} alt="Aurora borealis over an arctic landscape" caption="Solar cycle 25 · aurora activity intensifying" />

      <Chapter eyebrow="/06 · Protecting tomorrow" title="The best way to care for our planet is to understand it.">
        <p>
          By documenting Earth's landscapes, biodiversity, weather, and climate, we create a valuable record that can help future generations appreciate the beauty of nature, recognize environmental change, and make informed decisions about conservation and resilience.
        </p>
        <p>
          NaturePulse believes that every tree, every river, every species, every cloud, and every weather event has a story worth preserving.
        </p>
        <p>
          Together, we can build a living chronicle of our planet — one that inspires curiosity, supports science, encourages conservation, and reminds us that the health of Earth is a shared responsibility.
        </p>
      </Chapter>

      <ImagePlate src={IMG.turbines} alt="Wind turbines at sunset" caption="Renewables · 30.3% of global electricity" />

      {/* Signature block */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 pt-8 pb-32 md:pb-40 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
        >
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-6">/ Signature</div>
          <h2 className="font-serif-display text-6xl md:text-8xl leading-[0.9] tracking-tighter mb-6">
            <em className="text-[color:var(--np-brand)]">NaturePulse.</em>
          </h2>
          <p className="font-serif-display text-2xl md:text-3xl italic text-[color:var(--np-text)] max-w-3xl mx-auto leading-tight mb-8">
            Discover Nature. Follow the Weather. Understand Climate. Protect Our Future.
          </p>
          <p className="text-[color:var(--np-text-2)] max-w-2xl mx-auto text-lg">
            Every observation matters. Every day adds another page to the story of our planet.
          </p>
        </motion.div>
      </section>
    </TranslatedProse>
  );
}
